import React, { useId, useRef, useState } from 'react';
import { cn } from '../../lib/classNameHelpers';
import {
  Input as CatalystInput,
  InputProps as CatalystInputProps,
  InputInner,
} from '../catalyst-ui-kit/input';
import { CustomNumericFormat, CustomNumericFormatProps } from './CustomNumericFormat';

// label acts as the placeholder
type BaseInputProps = Omit<CatalystInputProps, 'placeholder'>;

type CommonInputProps = BaseInputProps & {
  label: string;
  ref?: React.Ref<HTMLInputElement>;
};

type TextInputProps = CommonInputProps & {
  inputType: 'text';
} & Omit<BaseInputProps, keyof CustomNumericFormatProps>;

type NumberInputProps = CommonInputProps & {
  inputType: 'number';
} & Omit<CustomNumericFormatProps, 'customInput' | 'getInputRef'>;

type InputProps = TextInputProps | NumberInputProps;

export const Input = ({
  id,
  label,
  hasError,
  inputType,
  className,
  leftElement,
  rightElement,
  containerClassName,
  ref,
  ...props
}: InputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const generatedId = useId();
  const inputId = id || generatedId;
  const labelId = `${inputId}-label`;

  // Merge the forwarded ref with our local ref
  const handleRef = (instance: HTMLInputElement | null) => {
    inputRef.current = instance;

    if (typeof ref === 'function') {
      ref(instance);
    } else if (ref) {
      ref.current = instance;
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    // Only focus if the input isn't already focused and the click wasn't on the input itself
    if (inputRef.current && e.target !== inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const hasValue = Boolean(props.value || props.defaultValue || inputRef.current?.value);
  const isFloating = isFocused || hasValue;

  const commonInputProps = {
    id: inputId,
    ref: handleRef,
    'aria-invalid': hasError,
    'aria-labelledby': labelId,
    'aria-describedby': hasError ? `${inputId}-error` : undefined,
    className: cn(
      'w-full transition-transform !p-0',
      isFloating ? 'transform translate-y-0' : 'transform -translate-y-1.5',
      className
    ),
    onFocus: handleFocus,
    onBlur: handleBlur,
  };

  return (
    <CatalystInput
      hasError={hasError}
      disabled={props.disabled}
      leftElement={leftElement}
      rightElement={rightElement}
      containerOnClick={handleContainerClick}
      containerClassName={containerClassName}
    >
      <div className="flex size-full flex-col items-start justify-stretch">
        {label && (
          <label
            id={labelId}
            htmlFor={inputId}
            className={cn(
              'h-3 text-contentSecondary transition-all origin-top-left',
              isFloating
                ? 'top-0 text-3xs transform scale-100'
                : 'top-0 text-xs transform translate-y-1.5 scale-100'
            )}
          >
            {label}
          </label>
        )}
        {inputType === 'number' ? (
          <CustomNumericFormat {...commonInputProps} {...(props as NumberInputProps)} />
        ) : (
          <InputInner {...commonInputProps} {...props} />
        )}
      </div>
    </CatalystInput>
  );
};
